import React, { useState } from "react";
import { WizardContextProvider, WizardView, useWizardPage } from "./Wizard";

/**
 * SampleWizard - Reference implementation of the Wizard component
 *
 * This wizard demonstrates:
 * - Multiple pages with different form controls
 * - Form state preservation when navigating back/forth
 * - Validation and conditional navigation
 * - Usage of useWizardPage hook
 * - Integration with manifest
 */

/**
 * Page 1: Personal Information
 */
function PersonalInfoPage({ manifest = {}, onNextRequested }) {
    const { useSyncedField } = useWizardPage();
    const [name, setName] = useSyncedField("name", "");
    const [email, setEmail] = useSyncedField("email", "");
    const [phone, setPhone] = useSyncedField("phone", "");
    const [errors, setErrors] = useState({});

    const handleNameChange = (e) => setName(e.target.value);
    const handleEmailChange = (e) => setEmail(e.target.value);
    const handlePhoneChange = (e) => setPhone(e.target.value);

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) newErrors.name = "Name is required";
        if (!email.trim()) newErrors.email = "Email is required";
        if (email && !email.includes("@"))
            newErrors.email = "Invalid email format";
        return newErrors;
    };

    React.useEffect(() => {
        if (onNextRequested) {
            const originalHandler = onNextRequested;
            const wrappedHandler = (event) => {
                const validationErrors = validateForm();
                if (Object.keys(validationErrors).length > 0) {
                    setErrors(validationErrors);
                    event.detail.prevented = true;
                    return;
                }
                setErrors({});
                originalHandler(event);
            };
            // This is handled by the parent component
        }
    }, [name, email, phone, onNextRequested]);

    return (
        <div className="card">
            <div className="card-header bg-light">
                <h3 className="card-title">
                    <i className="fa fa-user me-2"></i>
                    Personal Information
                </h3>
            </div>
            <div className="card-body">
                <p className="text-muted">
                    Please provide your basic personal information.
                </p>

                <div className="form-group mb-3">
                    <label htmlFor="name" className="form-label">
                        Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        className={`form-control ${
                            errors.name ? "is-invalid" : ""
                        }`}
                        placeholder="Enter your full name"
                        value={name}
                        onChange={handleNameChange}
                    />
                    {errors.name && (
                        <div className="invalid-feedback">{errors.name}</div>
                    )}
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                        id="email"
                        type="email"
                        className={`form-control ${
                            errors.email ? "is-invalid" : ""
                        }`}
                        placeholder="Enter your email address"
                        value={email}
                        onChange={handleEmailChange}
                    />
                    {errors.email && (
                        <div className="invalid-feedback">{errors.email}</div>
                    )}
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="phone" className="form-label">
                        Phone Number
                    </label>
                    <input
                        id="phone"
                        type="tel"
                        className="form-control"
                        placeholder="Enter your phone number"
                        value={phone}
                        onChange={handlePhoneChange}
                    />
                </div>

                <div className="alert alert-info">
                    <i className="fa fa-info-circle me-2"></i>
                    <strong>Note:</strong> Your information will be securely
                    stored and used only for the purpose of this wizard.
                </div>
            </div>
        </div>
    );
}

/**
 * Page 2: Preferences and Settings
 */
function PreferencesPage({ manifest, onNextRequested }) {
    const { useSyncedField } = useWizardPage();
    const [country, setCountry] = useSyncedField("country", "");
    const [industry, setIndustry] = useSyncedField("industry", "");
    const [notifications, setNotifications] = useSyncedField(
        "notifications",
        true
    );
    const [newsletter, setNewsletter] = useSyncedField("newsletter", true);

    const handleCountryChange = (e) => setCountry(e.target.value);

    const handleIndustryChange = (e) => setIndustry(e.target.value);

    const handleNotificationsChange = (e) => setNotifications(e.target.checked);

    const handleNewsletterChange = (e) => setNewsletter(e.target.checked);

    return (
        <div className="card">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h3 className="card-title mb-0">
                        <i className="fa fa-sliders-h me-2"></i>
                        Preferences & Settings
                    </h3>
                    <span className="badge bg-warning">Optional</span>
                </div>
            </div>
            <div className="card-body">
                <p className="text-muted">
                    Customize your experience with these preferences. You can
                    skip this step if you prefer.
                </p>

                <div className="form-group mb-3">
                    <label htmlFor="country" className="form-label">
                        Country
                    </label>
                    <select
                        id="country"
                        className="form-control"
                        value={country}
                        onChange={handleCountryChange}
                    >
                        <option value="">Select a country</option>
                        <option value="us">United States</option>
                        <option value="uk">United Kingdom</option>
                        <option value="ca">Canada</option>
                        <option value="au">Australia</option>
                        <option value="in">India</option>
                        <option value="de">Germany</option>
                        <option value="fr">France</option>
                    </select>
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="industry" className="form-label">
                        Industry
                    </label>
                    <select
                        id="industry"
                        className="form-control"
                        value={industry}
                        onChange={handleIndustryChange}
                    >
                        <option value="">Select an industry</option>
                        <option value="tech">Technology</option>
                        <option value="finance">Finance</option>
                        <option value="healthcare">Healthcare</option>
                        <option value="retail">Retail</option>
                        <option value="manufacturing">Manufacturing</option>
                        <option value="education">Education</option>
                    </select>
                </div>

                <div className="form-group mb-3">
                    <div className="custom-control custom-checkbox">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id="notifications"
                            checked={notifications}
                            onChange={handleNotificationsChange}
                        />
                        <label
                            className="custom-control-label"
                            htmlFor="notifications"
                        >
                            Enable email notifications
                        </label>
                    </div>
                </div>

                <div className="form-group mb-3">
                    <div className="custom-control custom-checkbox">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id="newsletter"
                            checked={newsletter}
                            onChange={handleNewsletterChange}
                        />
                        <label
                            className="custom-control-label"
                            htmlFor="newsletter"
                        >
                            Subscribe to our newsletter
                        </label>
                    </div>
                </div>

                <div className="alert alert-warning">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    <strong>Reminder:</strong> You can change these preferences
                    anytime in your account settings.
                </div>
            </div>
        </div>
    );
}

/**
 * Page 3: Billing Information
 */
function BillingPage({ manifest = {}, onNextRequested }) {
    const { useSyncedField } = useWizardPage();
    const [billingName, setBillingName] = useSyncedField("billingName", "");
    const [address, setAddress] = useSyncedField("address", "");
    const [city, setCity] = useSyncedField("city", "");
    const [zipCode, setZipCode] = useSyncedField("zipCode", "");
    const [cardType, setCardType] = useSyncedField("cardType", "");

    const handleBillingNameChange = (e) => setBillingName(e.target.value);

    const handleAddressChange = (e) => setAddress(e.target.value);

    const handleCityChange = (e) => setCity(e.target.value);

    const handleZipCodeChange = (e) => setZipCode(e.target.value);

    const handleCardTypeChange = (e) => setCardType(e.target.value);

    return (
        <div className="card">
            <div className="card-header bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <h3 className="card-title mb-0">
                        <i className="fa fa-credit-card me-2"></i>
                        Billing Information
                    </h3>
                    <span className="badge bg-warning">Optional</span>
                </div>
            </div>
            <div className="card-body">
                <p className="text-muted">
                    Enter your billing and payment details. This step can be
                    skipped for now and completed later.
                </p>

                <div className="form-group mb-3">
                    <label htmlFor="billingName" className="form-label">
                        Billing Name
                    </label>
                    <input
                        id="billingName"
                        type="text"
                        className="form-control"
                        placeholder="Name on billing account"
                        value={billingName}
                        onChange={handleBillingNameChange}
                    />
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="address" className="form-label">
                        Street Address
                    </label>
                    <input
                        id="address"
                        type="text"
                        className="form-control"
                        placeholder="Enter street address"
                        value={address}
                        onChange={handleAddressChange}
                    />
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="city" className="form-label">
                                City
                            </label>
                            <input
                                id="city"
                                type="text"
                                className="form-control"
                                placeholder="Enter city"
                                value={city}
                                onChange={handleCityChange}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group mb-3">
                            <label htmlFor="zipCode" className="form-label">
                                ZIP Code
                            </label>
                            <input
                                id="zipCode"
                                type="text"
                                className="form-control"
                                placeholder="Enter ZIP code"
                                value={zipCode}
                                onChange={handleZipCodeChange}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-group mb-3">
                    <label htmlFor="cardType" className="form-label">
                        Card Type
                    </label>
                    <select
                        id="cardType"
                        className="form-control"
                        value={cardType}
                        onChange={handleCardTypeChange}
                    >
                        <option value="">Select a payment method</option>
                        <option value="visa">Visa</option>
                        <option value="mastercard">Mastercard</option>
                        <option value="amex">American Express</option>
                        <option value="discover">Discover</option>
                    </select>
                </div>

                <div className="alert alert-info">
                    <i className="fa fa-lock me-2"></i>
                    <strong>Secure:</strong> Your payment information is
                    encrypted and secured with industry-standard protocols.
                </div>
            </div>
        </div>
    );
}

/**
 * Page 4: Review & Summary
 */
function ReviewPage({ manifest = {}, onFinishRequested }) {
    return (
        <div className="card">
            <div className="card-header bg-light">
                <h3 className="card-title">
                    <i className="fa fa-check-circle me-2"></i>
                    Review & Confirm
                </h3>
            </div>
            <div className="card-body">
                <p className="text-muted mb-4">
                    Please review your information before completing the wizard.
                </p>

                <div className="row mb-4">
                    <div className="col-md-6">
                        <h5 className="text-primary">Personal Information</h5>
                        <dl className="row">
                            <dt className="col-sm-5">Name:</dt>
                            <dd className="col-sm-7">
                                {manifest?.name || "Not provided"}
                            </dd>
                            <dt className="col-sm-5">Email:</dt>
                            <dd className="col-sm-7">
                                {manifest?.email || "Not provided"}
                            </dd>
                            <dt className="col-sm-5">Phone:</dt>
                            <dd className="col-sm-7">
                                {manifest?.phone || "Not provided"}
                            </dd>
                        </dl>
                    </div>
                    <div className="col-md-6">
                        <h5 className="text-primary">Preferences</h5>
                        <dl className="row">
                            <dt className="col-sm-5">Country:</dt>
                            <dd className="col-sm-7">
                                {manifest?.country || "Not selected"}
                            </dd>
                            <dt className="col-sm-5">Industry:</dt>
                            <dd className="col-sm-7">
                                {manifest?.industry || "Not selected"}
                            </dd>
                            <dt className="col-sm-5">Notifications:</dt>
                            <dd className="col-sm-7">
                                {manifest?.notifications
                                    ? "Enabled"
                                    : "Disabled"}
                            </dd>
                        </dl>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <h5 className="text-primary">Billing Address</h5>
                        <dl className="row">
                            <dt className="col-sm-5">Name:</dt>
                            <dd className="col-sm-7">
                                {manifest?.billingName || "Not provided"}
                            </dd>
                            <dt className="col-sm-5">Address:</dt>
                            <dd className="col-sm-7">
                                {manifest?.address || "Not provided"}
                            </dd>
                            <dt className="col-sm-5">City:</dt>
                            <dd className="col-sm-7">
                                {manifest?.city || "Not provided"}
                            </dd>
                            <dt className="col-sm-5">ZIP:</dt>
                            <dd className="col-sm-7">
                                {manifest?.zipCode || "Not provided"}
                            </dd>
                        </dl>
                    </div>
                    <div className="col-md-6">
                        <h5 className="text-primary">Payment Method</h5>
                        <dl className="row">
                            <dt className="col-sm-5">Card Type:</dt>
                            <dd className="col-sm-7">
                                {manifest?.cardType || "Not selected"}
                            </dd>
                        </dl>
                    </div>
                </div>

                <div className="alert alert-success mt-4">
                    <i className="fa fa-check me-2"></i>
                    <strong>Ready to go!</strong> Click the Finish button below
                    to complete the wizard.
                </div>
            </div>
        </div>
    );
}

/**
 * Main SampleWizard Component
 */
export default function SampleWizard() {
    const [completedManifest, setCompletedManifest] = useState(null);

    const handleClose = (manifest) => {
        console.log("Wizard closed with manifest:", manifest);
        alert(`Wizard cancelled. Current data: ${JSON.stringify(manifest)}`);
    };

    const handleFinish = (manifest) => {
        console.log("Wizard finished with manifest:", manifest);
        setCompletedManifest(manifest);
        alert(`Wizard completed! Data: ${JSON.stringify(manifest, null, 2)}`);
    };

    if (completedManifest) {
        return (
            <div className="container mt-5">
                <div className="card">
                    <div className="card-header bg-success">
                        <h3 className="card-title text-white">
                            <i className="fa fa-check me-2"></i>
                            Wizard Completed Successfully!
                        </h3>
                    </div>
                    <div className="card-body">
                        <p className="mb-3">
                            Thank you for completing the wizard. Here's a
                            summary of your information:
                        </p>
                        <pre className="bg-light p-3 rounded">
                            {JSON.stringify(completedManifest, null, 2)}
                        </pre>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={() => setCompletedManifest(null)}
                        >
                            Start New Wizard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <WizardContextProvider
            title="Sample Registration Wizard"
            onClose={handleClose}
            onFinish={handleFinish}
        >
            <WizardView.Page title="Personal Info" icon="fa-user">
                <PersonalInfoPage />
            </WizardView.Page>

            <WizardView.Page
                title="Preferences"
                icon="fa-sliders-h"
                skippable={true}
            >
                <PreferencesPage />
            </WizardView.Page>

            <WizardView.Page
                title="Billing"
                icon="fa-credit-card"
                skippable={true}
            >
                <BillingPage />
            </WizardView.Page>

            <WizardView.Page title="Review" icon="fa-check-circle">
                <ReviewPage />
            </WizardView.Page>
        </WizardContextProvider>
    );
}
